import React, { useCallback } from 'react';
import { User } from 'lucide-react';
import { InfiniteScroll } from '@/components/common/InfiniteScroll/InfiniteScroll';
import { TUserItem, RequestParams } from '@/data/types';
import UserGridCard from './UserGridCard';
import UserListRow from './UserListRow';

interface PersonnelListProps {
  attemptGetAllUsers: (params: RequestParams) => Promise<any>;
  usersData: TUserItem[] | undefined;
  viewMode: 'grid' | 'list';
  searchTerm: string;
  // Helper functions passed down to avoid re-creating them
  getUserInitials: (user: TUserItem) => string;
  getUserRole: (user: TUserItem) => string;
  getUserDepartment: (user: TUserItem) => string;
  getUserStatus: (user: TUserItem) => 'active' | 'inactive';
  getUserRating: (user: TUserItem) => number;
  getUserProjectsCount: (user: TUserItem) => number;
  getPermissionsCount: (user: TUserItem) => number;
  formatJoinDate: (dateString: string) => string;
  getStatusColor: (status: string) => string;
  getDepartmentColor: (department: string) => string;
}

const PersonnelList: React.FC<PersonnelListProps> = React.memo(
  ({
    attemptGetAllUsers,
    usersData = [],
    viewMode,
    searchTerm,
    getUserInitials,
    getUserRole,
    getUserDepartment,
    getUserStatus,
    getUserRating,
    getUserProjectsCount,
    getPermissionsCount,
    formatJoinDate,
    getStatusColor,
    getDepartmentColor,
  }) => {
    // Render function for users
    const renderUsers = useCallback(
      (users: TUserItem[], state: any) => {
        if (users.length === 0 && !state.loading) {
          return (
            <div className="rounded-xl border border-gray-200 bg-white p-12 text-center shadow-sm">
              <div className="mx-auto h-16 w-16 rounded-full bg-gray-100 p-4">
                <User className="h-8 w-8 text-gray-400" />
              </div>
              <h3 className="mt-4 text-lg font-medium text-gray-900">Δεν βρέθηκαν χρήστες</h3>
              <p className="mt-2 text-gray-500">
                {searchTerm
                  ? 'Δοκιμάστε να προσαρμόσετε τους όρους αναζήτησής σας.'
                  : 'Ξεκινήστε προσθέτοντας το πρώτο μέλος της ομάδας σας.'}
              </p>
            </div>
          );
        }

        if (viewMode === 'grid') {
          return (
            <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
              {users.map((user, index) => (
                <UserGridCard
                  key={user.id}
                  user={user}
                  index={index}
                  getUserInitials={getUserInitials}
                  getUserRole={getUserRole}
                  getUserDepartment={getUserDepartment}
                  getUserStatus={getUserStatus}
                  getUserRating={getUserRating}
                  getUserProjectsCount={getUserProjectsCount}
                  getPermissionsCount={getPermissionsCount}
                  formatJoinDate={formatJoinDate}
                  getStatusColor={getStatusColor}
                  getDepartmentColor={getDepartmentColor}
                />
              ))}
            </div>
          );
        } else {
          return (
            <div className="rounded-xl border border-gray-200 bg-white shadow-sm">
              <div className="grid grid-cols-10 gap-4 border-b border-gray-200 px-6 py-4 text-sm font-medium text-gray-500">
                <div className="col-span-3">Όνομα</div>
                <div className="col-span-2">Ρόλος</div>
                <div className="col-span-2">Τμήμα</div>
                <div className="col-span-2">Τηλέφωνο</div>
                <div className="col-span-1">Κατάσταση</div>
              </div>
              {users.map((user, index) => (
                <UserListRow
                  key={user.id}
                  user={user}
                  index={index}
                  getUserInitials={getUserInitials}
                  getUserRole={getUserRole}
                  getUserDepartment={getUserDepartment}
                  getUserStatus={getUserStatus}
                  getUserProjectsCount={getUserProjectsCount}
                  getStatusColor={getStatusColor}
                  getDepartmentColor={getDepartmentColor}
                />
              ))}
            </div>
          );
        }
      },
      [
        viewMode,
        searchTerm,
        getUserInitials,
        getUserRole,
        getUserDepartment,
        getUserStatus,
        getUserRating,
        getUserProjectsCount,
        getPermissionsCount,
        formatJoinDate,
        getStatusColor,
        getDepartmentColor,
      ],
    );

    // Loading component
    const renderLoading = useCallback((isInitial: boolean) => {
      if (isInitial) {
        return (
          <div className="flex items-center justify-center py-20">
            <div className="flex flex-col items-center">
              <div className="mb-4 h-12 w-12 animate-spin rounded-full border-4 border-blue-600 border-t-transparent"></div>
              <p className="text-gray-600">Φόρτωση προσωπικού...</p>
            </div>
          </div>
        );
      }

      return (
        <div className="flex items-center justify-center py-8">
          <div className="mr-3 h-8 w-8 animate-spin rounded-full border-4 border-blue-600 border-t-transparent"></div>
          <p className="text-gray-600">Φόρτωση περισσότερων...</p>
        </div>
      );
    }, []);

    // Error component
    const renderError = useCallback((error: string, retry: () => void) => {
      return (
        <div className="flex flex-col items-center justify-center py-20">
          <div className="mb-4 flex h-20 w-20 items-center justify-center rounded-full bg-red-100">
            <svg className="h-8 w-8 text-red-500" fill="currentColor" viewBox="0 0 20 20">
              <path
                fillRule="evenodd"
                d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
                clipRule="evenodd"
              />
            </svg>
          </div>
          <h3 className="mb-2 text-lg font-medium text-gray-900">Σφάλμα φόρτωσης</h3>
          <p className="mb-4 max-w-md text-center text-gray-500">{error}</p>
          <button
            onClick={retry}
            className="rounded-lg bg-blue-600 px-4 py-2 text-white transition-colors hover:bg-blue-700"
          >
            Δοκιμάστε ξανά
          </button>
        </div>
      );
    }, []);

    return (
      <InfiniteScroll
        apiAction={attemptGetAllUsers}
        itemsData={usersData}
        initialPageSize={20}
        debug={false}
        renderLoading={renderLoading}
        search={{
          searchValue: searchTerm,
          debounceMs: 300,
          resetOnChange: true,
        }}
        renderError={renderError}
        scroll={{
          threshold: 300,
          debounceMs: 200,
        }}

        className="pb-8"
      >
        {renderUsers}
      </InfiniteScroll>
    );
  },
);

PersonnelList.displayName = 'PersonnelList';

export default PersonnelList;
